import { useEffect, useState } from 'react';
import { Braces } from 'lucide-react';
import { Button, Alert } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { errText } from '@/lib/workflowBuilderHooks';

/**
 * The LLM App spec editor — the declarative authoring surface for a workflow of kind
 * llm_app. The spec (services/workflows/llmAppSpec.js) is the source of truth: Save
 * compiles it into the graph server-side, so the canvas on the Build tab mirrors it and
 * must not be hand-edited (a canvas Save recompiles from the spec again).
 *
 * Spec shape:
 *   { mode: 'llm'|'agent', agent_key, system_prompt, user_prompt,
 *     input: {name: {type, description, optional}}, output: {...},
 *     client_tools: {tool: {name, description, arguments}} }
 *
 * `client_tools` are executed by the CALLING application: a run pauses at
 * waiting_for_client, the app POSTs results to /api/v1/runs/{id}/tool-results, the run
 * resumes. See docs/PUBLIC-API.md.
 */

const textareaClass = 'w-full font-mono text-xs rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] px-2 py-1.5 min-h-[420px]';

function prettySpec(raw) {
  try {
    return JSON.stringify(typeof raw === 'string' ? JSON.parse(raw) : raw, null, 2);
  } catch {
    return typeof raw === 'string' ? raw : '';
  }
}

export default function LlmAppSpecPanel({ form, agents, onSaved }) {
  const { fetchApi } = useAppApi();
  const [text, setText] = useState(prettySpec(form.spec_json));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  // Reset the editor when the underlying workflow changes under us (reload after save).
  useEffect(() => {
    setText(prettySpec(form.spec_json));
    setDirty(false);
    setErr('');
    setNotice('');
  }, [form.spec_json]);

  const save = async () => {
    setSaving(true);
    setErr('');
    setNotice('');
    try {
      JSON.parse(text); // a local syntax error should say so before the server round-trips
      await fetchApi('crud', {
        model: 'AppWorkflow',
        action: 'update',
        where: { id: form.id },
        data: { spec_json: text },
      });
      setNotice('Saved — the graph on the Build tab was recompiled from this spec.');
      setDirty(false);
      if (typeof onSaved === 'function') onSaved();
    } catch (ex) {
      setErr(ex instanceof SyntaxError ? `Spec is not valid JSON: ${ex.message}` : errText(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 max-w-3xl">
      <Alert variant="info" title="LLM App — intelligence endpoint">
        This workflow is compiled from the spec below (input → LLM/agent → output). Third-party
        apps run it with <code className="font-mono">POST /api/v1/workflows/&#123;key&#125;/run</code>;
        declared <code className="font-mono">client_tools</code> are executed by the calling
        application (the run pauses and resumes when it POSTs the results).
      </Alert>

      {err && <Alert variant="error" title="Could not save">{err}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      <label className="block space-y-1">
        <span className="flex items-center gap-1 text-sm font-medium"><Braces size={13} /> Spec</span>
        <textarea
          className={textareaClass}
          spellCheck={false}
          value={text}
          onChange={(e) => { setText(e.target.value); setDirty(true); }}
        />
      </label>

      <p className="text-xs text-secondary-500">
        <code className="font-mono">user_prompt</code> references inputs with <code className="font-mono">&#123;&#123;input.field&#125;&#125;</code>.
        An <code className="font-mono">output</code> field list is enforced as structured JSON on the model's
        answer. <code className="font-mono">client_tools.arguments</code> is a JSON-Schema properties map —
        the calling app runs the tools and posts <code className="font-mono">&#123;results: [&#123;callId, result&#125;]&#125;</code>.
        Agent mode names the specialist with <code className="font-mono">agent_key</code>.
      </p>

      <div className="flex items-center justify-end gap-2">
        {agents.length > 0 && (
          <span className="text-xs text-secondary-500">
            Agents available to agent mode: {agents.slice(0, 4).map((a) => a.name).join(', ')}
            {agents.length > 4 ? ` +${agents.length - 4}` : ''}
          </span>
        )}
        <Button size="sm" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save spec'}</Button>
      </div>
    </div>
  );
}
